
import React from "react";
import {
  Eye,
  Pencil,
  Trash2,
  Send,
  Archive,
  RotateCcw,
  Power,
  BarChart3,
  Clock3,
  FileQuestion,
  Trophy,
  CalendarDays,
} from "lucide-react";

// ======================================================
// Helpers
// ======================================================

const statusStyles = {
  draft: {
    container:
      "bg-gray-100 text-gray-700 ring-gray-200",
    dot: "bg-gray-500",
  },

  published: {
    container:
      "bg-emerald-100 text-emerald-700 ring-emerald-200",
    dot: "bg-emerald-500",
  },

  archived: {
    container:
      "bg-amber-100 text-amber-700 ring-amber-200",
    dot: "bg-amber-500",
  },

  active: {
    container:
      "bg-blue-100 text-blue-700 ring-blue-200",
    dot: "bg-blue-500",
  },

  inactive: {
    container:
      "bg-red-100 text-red-700 ring-red-200",
    dot: "bg-red-500",
  },
};

const normalizeStatus = (status) =>
  String(status || "draft").toLowerCase();

const formatDate = (date) => {
  if (!date) return "—";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "—";
  }

  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getQuizId = (quiz) =>
  quiz?._id || quiz?.id;

const getSubjectName = (quiz) => {
  if (!quiz?.questionBankSubject) {
    return "—";
  }

  if (
    typeof quiz.questionBankSubject ===
    "object"
  ) {
    return (
      quiz.questionBankSubject.name ||
      quiz.questionBankSubject.title ||
      quiz.questionBankSubject._id ||
      "—"
    );
  }

  return quiz.questionBankSubject;
};

const getTotalQuestions = (quiz) => {
  if (
    quiz?.totalQuestions !== undefined &&
    quiz?.totalQuestions !== null
  ) {
    return quiz.totalQuestions;
  }

  if (
    quiz?.questionCount !== undefined &&
    quiz?.questionCount !== null
  ) {
    return quiz.questionCount;
  }

  return "—";
};

const getTotalMarks = (quiz) => {
  if (
    quiz?.totalMarks !== undefined &&
    quiz?.totalMarks !== null
  ) {
    return quiz.totalMarks;
  }

  if (
    quiz?.marks !== undefined &&
    quiz?.marks !== null
  ) {
    return quiz.marks;
  }

  return "—";
};

// ======================================================
// Small Info Item
// ======================================================

const InfoItem = ({
  icon: Icon,
  label,
  value,
}) => (
  <div className="flex items-center gap-2">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-500">
      <Icon size={15} />
    </div>

    <div className="min-w-0">
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
        {label}
      </p>

      <p className="truncate text-sm font-semibold text-gray-800">
        {value}
      </p>
    </div>
  </div>
);

// ======================================================
// Action Button
// ======================================================

const ActionButton = ({
  title,
  onClick,
  children,
  className = "",
  disabled = false,
}) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex h-9 w-9 items-center justify-center rounded-lg
      border border-gray-200 bg-white text-gray-600
      shadow-sm transition
      hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900
      disabled:cursor-not-allowed disabled:opacity-40
      ${className}`}
  >
    {children}
  </button>
);

// ======================================================
// English Quiz Card
// ======================================================

const EnglishQuizCard = ({
  quiz,

  onView,
  onEdit,
  onDelete,

  onPublish,
  onUnpublish,
  onArchive,
  onActivate,

  onStatistics,

  deletingQuizId = null,
  publishingQuizId = null,
  unpublishingQuizId = null,
  archivingQuizId = null,
  activatingQuizId = null,
}) => {
  if (!quiz) {
    return null;
  }

  const quizId = getQuizId(quiz);

  const status = normalizeStatus(
    quiz.status,
  );

  const statusStyle =
    statusStyles[status] ||
    statusStyles.draft;

  const isPublished =
    status === "published";

  const isArchived =
    status === "archived";

  const isDeleting =
    deletingQuizId === quizId;

  const isPublishing =
    publishingQuizId === quizId;

  const isUnpublishing =
    unpublishingQuizId === quizId;

  const isArchiving =
    archivingQuizId === quizId;

  const isActivating =
    activatingQuizId === quizId;

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md">
      {/* ==================================================
          Header
      ================================================== */}

      <div className="border-b border-gray-100 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() =>
                onView?.(quiz)
              }
              className="block max-w-full text-left"
            >
              <h3 className="truncate text-base font-bold text-gray-900 transition hover:text-blue-600">
                {quiz.name ||
                  "Untitled English Quiz"}
              </h3>
            </button>

            {quiz.description && (
              <p className="mt-1.5 line-clamp-2 text-sm leading-5 text-gray-500">
                {quiz.description}
              </p>
            )}
          </div>

          <span
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${statusStyle.container}`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${statusStyle.dot}`}
            />

            {status
              .charAt(0)
              .toUpperCase() +
              status.slice(1)}
          </span>
        </div>

        {/* Subject */}
        <div className="mt-4 flex items-center gap-2">
          <span className="rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
            {getSubjectName(quiz)}
          </span>

          {!quiz.isActive &&
            !isArchived && (
              <span className="rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
                Inactive
              </span>
            )}
        </div>
      </div>

      {/* ==================================================
          Quiz Statistics
      ================================================== */}

      <div className="grid grid-cols-2 gap-px border-b border-gray-100 bg-gray-100">
        <div className="bg-white px-4 py-4">
          <InfoItem
            icon={FileQuestion}
            label="Questions"
            value={getTotalQuestions(
              quiz,
            )}
          />
        </div>

        <div className="bg-white px-4 py-4">
          <InfoItem
            icon={Trophy}
            label="Total Marks"
            value={getTotalMarks(quiz)}
          />
        </div>

        <div className="bg-white px-4 py-4">
          <InfoItem
            icon={Clock3}
            label="Duration"
            value={
              quiz.durationMinutes
                ? `${quiz.durationMinutes} min`
                : "—"
            }
          />
        </div>

        <div className="bg-white px-4 py-4">
          <InfoItem
            icon={Trophy}
            label="Passing"
            value={
              quiz.passingPercentage !==
              undefined &&
              quiz.passingPercentage !==
                null
                ? `${quiz.passingPercentage}%`
                : "—"
            }
          />
        </div>
      </div>

      {/* ==================================================
          Meta
      ================================================== */}

      <div className="flex items-center justify-between gap-3 px-5 py-3">
        <div className="flex min-w-0 items-center gap-2 text-xs text-gray-500">
          <CalendarDays
            size={14}
            className="shrink-0"
          />

          <span className="truncate">
            Created{" "}
            {formatDate(
              quiz.createdAt,
            )}
          </span>
        </div>

        {quiz.maxAttempts ? (
          <span className="shrink-0 rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-500">
            {quiz.maxAttempts}{" "}
            {quiz.maxAttempts === 1
              ? "attempt"
              : "attempts"}
          </span>
        ) : quiz.allowMultipleAttempts ? (
          <span className="shrink-0 rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-600">
            Multiple attempts
          </span>
        ) : (
          <span className="shrink-0 rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-500">
            Single attempt
          </span>
        )}
      </div>

      {/* ==================================================
          Actions
      ================================================== */}

      <div className="mt-auto border-t border-gray-100 bg-gray-50/70 px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            {onView && (
              <ActionButton
                title="View quiz"
                onClick={() =>
                  onView(quiz)
                }
              >
                <Eye size={16} />
              </ActionButton>
            )}

            {onEdit &&
              !isPublished && (
                <ActionButton
                  title="Edit quiz"
                  onClick={() =>
                    onEdit(quiz)
                  }
                >
                  <Pencil size={16} />
                </ActionButton>
              )}

            {onStatistics && (
              <ActionButton
                title="View statistics"
                onClick={() =>
                  onStatistics(quiz)
                }
                className="text-purple-600 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-700"
              >
                <BarChart3 size={16} />
              </ActionButton>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            {onPublish &&
              status === "draft" && (
                <ActionButton
                  title="Publish quiz"
                  onClick={() =>
                    onPublish(quiz)
                  }
                  disabled={
                    isPublishing
                  }
                  className="text-emerald-600 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
                >
                  <Send size={16} />
                </ActionButton>
              )}

            {onUnpublish &&
              isPublished && (
                <ActionButton
                  title="Unpublish quiz"
                  onClick={() =>
                    onUnpublish(quiz)
                  }
                  disabled={
                    isUnpublishing
                  }
                  className="text-orange-600 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700"
                >
                  <RotateCcw
                    size={16}
                  />
                </ActionButton>
              )}

            {onArchive &&
              !isArchived && (
                <ActionButton
                  title="Archive quiz"
                  onClick={() =>
                    onArchive(quiz)
                  }
                  disabled={
                    isArchiving
                  }
                  className="text-amber-600 hover:border-amber-200 hover:bg-amber-50 hover:text-amber-700"
                >
                  <Archive size={16} />
                </ActionButton>
              )}

            {onActivate &&
              isArchived && (
                <ActionButton
                  title="Activate quiz"
                  onClick={() =>
                    onActivate(quiz)
                  }
                  disabled={
                    isActivating
                  }
                  className="text-blue-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                >
                  <Power size={16} />
                </ActionButton>
              )}

            {onDelete && (
              <ActionButton
                title="Delete quiz"
                onClick={() =>
                  onDelete(quiz)
                }
                disabled={
                  isDeleting ||
                  isPublished
                }
                className="text-red-600 hover:border-red-200 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 size={16} />
              </ActionButton>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default EnglishQuizCard;

